import React from "react";
import { Link } from "react-router-dom";
import { EVENT_LABELS } from "./activityLogLabels";

export function parseChangedData(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return { summary: String(raw) };
  try {
    return JSON.parse(raw);
  } catch {
    return { summary: raw };
  }
}

function isSystemName(name) {
  const s = name != null ? String(name).trim() : "";
  if (!s) return true;
  return /^system(\b|\s|\(|$)/i.test(s) || s.toUpperCase() === "SYSTEM";
}

export function displayActor(row) {
  const candidates = [
    row.actorName,
    row.actor?.userName,
    [row.actor?.firstName, row.actor?.lastName].filter(Boolean).join(" "),
    row.actor?.email,
    row.changedDataParsed?.performedBy?.name,
    row.changedDataParsed?.performedBy?.email,
  ];
  for (const c of candidates) {
    const s = c != null ? String(c).trim() : "";
    if (!s || isSystemName(s) || /^automatic$/i.test(s)) continue;
    return s;
  }
  if (row.userId) return `User #${row.userId}`;
  return "Automatic";
}

export function displayRole(row) {
  const r = row.userRole || row.actor?.userRole;
  if (!r || String(r).toUpperCase() === "SYSTEM") return "—";
  return String(r);
}

export function eventLabel(eventType) {
  if (!eventType) return "";
  return EVENT_LABELS[eventType] || eventType.replace(/_/g, " ").toLowerCase();
}

export function actionBadgeClass(action) {
  switch (action) {
    case "Add":
      return "log-report-action log-report-action--add";
    case "Delete":
      return "log-report-action log-report-action--delete";
    case "Edit":
      return "log-report-action log-report-action--edit";
    case "Login":
      return "log-report-action log-report-action--login";
    case "Logout":
      return "log-report-action log-report-action--logout";
    case "Assign":
      return "log-report-action log-report-action--assign";
    case "Access":
      return "log-report-action log-report-action--access";
    case "Publish":
      return "log-report-action log-report-action--publish";
    case "Close":
      return "log-report-action log-report-action--close";
    case "Notify":
      return "log-report-action log-report-action--notify";
    case "Lifecycle":
      return "log-report-action log-report-action--lifecycle";
    default:
      return "log-report-action log-report-action--default";
  }
}

function isBlankish(v) {
  if (v == null) return true;
  if (typeof v === "string") {
    const t = v.trim();
    return (
      t === "" ||
      t.toLowerCase() === "null" ||
      t.toLowerCase() === "undefined" ||
      t === "Nil" ||
      t === "—" ||
      t === "-" ||
      t === "[]" ||
      t === "{}"
    );
  }
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function displayVal(v) {
  if (isBlankish(v)) return "—";
  if (typeof v === "object") {
    if (v.coupleName || v.name) return v.coupleName || v.name;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v).trim();
  // Prefer readable dates over raw ISO
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
  }
  if (
    (s.startsWith("{") && s.endsWith("}")) ||
    (s.startsWith("[") && s.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(s);
      if (isBlankish(parsed)) return "—";
      if (Array.isArray(parsed)) {
        return (
          parsed
            .map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x)))
            .filter((x) => x && x.toLowerCase() !== "null")
            .join(", ") || "—"
        );
      }
      return JSON.stringify(parsed);
    } catch {
      /* keep */
    }
  }
  return s;
}

/** True change (not empty→empty or null→"null"). */
function isMeaningfulChange(c) {
  if (!c) return false;
  const oldD = displayVal(c.old);
  const newD = displayVal(c.new);
  if (oldD === newD) return false;
  if (oldD === "—" && newD === "—") return false;
  return true;
}

/**
 * When older logs stuffed diffs into summary ("Name — Field: a → b; …"),
 * keep only the identity part so we don't show changes twice.
 */
function identityFromSummary(summary, hasChanges) {
  if (!summary) return "";
  const s = String(summary);
  if (!hasChanges) return s;
  const emDash = s.indexOf(" — ");
  if (emDash > 0) {
    const after = s.slice(emDash + 3);
    if (/→|:/.test(after)) return s.slice(0, emDash).trim();
  }
  // If summary is only "Field: a → b; …" with no identity, hide it
  if (/→/.test(s) && /:/.test(s)) return "";
  return s;
}

function extraFacts(p) {
  const summary = String(p.summary || "");
  const facts = [];
  const already = (value) =>
    Boolean(value) &&
    summary.toLowerCase().includes(String(value).toLowerCase());
  const push = (label, value) => {
    const v = value != null ? String(value).trim() : "";
    if (!v || already(v)) return;
    facts.push({ label, value: v });
  };

  const retreat =
    p.retreatLabel ||
    (p.retreatTitle
      ? `${p.retreatTitle}${p.retreatId != null ? ` (Retreat ID ${p.retreatId})` : ""}`
      : p.retreatId != null
        ? `Retreat ID ${p.retreatId}`
        : "");

  let couple = String(
    p.coupleName || p.next?.coupleName || p.previous?.coupleName || "",
  ).trim();
  if (!couple && (p.record === "Couple" || p.primaryKey != null) && p.name) {
    couple = String(p.name);
  }
  if (
    couple &&
    p.primaryKey != null &&
    !String(couple).includes(`(${p.primaryKey})`)
  ) {
    couple = `${couple} (${p.primaryKey})`;
  }

  push("Retreat", retreat);
  // Couple/priest/etc. usually already sit in the summary identity line
  if (!couple || !summary.includes(couple.split(" · ")[0])) {
    push("Couple", couple);
  }
  push(
    "Priest",
    String(p.priestName || (p.record === "Priest" ? p.name : "") || "").trim(),
  );
  push(
    "Parish",
    String(
      p.parishName ||
        p.parishLabel ||
        (p.record === "Parish" ? p.name : "") ||
        "",
    ).trim(),
  );
  push("Role", String(p.record === "Role" ? p.name || "" : p.role || "").trim());
  push(
    "Ticket",
    String(
      p.ticketSubject
        ? p.recordId != null
          ? `${p.ticketSubject} (ID ${p.recordId})`
          : p.ticketSubject
        : p.record === "Ticket"
          ? p.name || ""
          : "",
    ).trim(),
  );
  push("Service", String(p.record === "Service" ? p.name || "" : "").trim());
  push("Talk", String(p.record === "Talk" ? p.name || "" : "").trim());
  push(
    "Parish category",
    String(p.record === "Parish category" ? p.name || "" : "").trim(),
  );

  if (p.assignedRole) {
    const roleAlreadyInSummary =
      summary &&
      summary.toLowerCase().includes(String(p.assignedRole).toLowerCase());
    if (!roleAlreadyInSummary) {
      push(
        p.roleVerb === "removed" ? "Role removed" : "Role",
        p.assignedRole,
      );
    }
  }
  if (p.sentBy) push("Sent by", p.sentBy);
  if (p.emailTo) push("To", p.emailTo);
  if (p.emailSubject) push("Subject", p.emailSubject);

  return facts;
}

/** Prefer set/clear wording over "— → value" for one-sided diffs. */
function formatChangePresentation(c, summaryText = "") {
  const field = c.field || c.key || "Field";
  const oldD = displayVal(c.old);
  const newD = displayVal(c.new);
  const summary = String(summaryText || "");

  // Skip assign-style Role rows when the summary already says the role
  if (
    String(field).toLowerCase() === "role" &&
    ((oldD === "—" && newD !== "—" && summary.toLowerCase().includes(newD.toLowerCase())) ||
      (newD === "—" && oldD !== "—" && summary.toLowerCase().includes(oldD.toLowerCase())))
  ) {
    return null;
  }

  if (oldD === "—" && newD !== "—") {
    return { mode: "set", field, value: newD };
  }
  if (newD === "—" && oldD !== "—") {
    return { mode: "clear", field, value: oldD };
  }
  return { mode: "change", field, old: oldD, new: newD };
}

export function activitySummaryText(raw, parsed) {
  const p = parsed || parseChangedData(raw);
  if (!p) return "";
  const meaningful = Array.isArray(p.changes)
    ? p.changes.filter(isMeaningfulChange)
    : [];
  const identity = identityFromSummary(p.summary, meaningful.length > 0);
  const parts = [identity].filter(Boolean);
  for (const f of extraFacts({ ...p, summary: identity })) {
    parts.push(`${f.label}: ${f.value}`);
  }
  if (meaningful.length) {
    const changeParts = meaningful
      .map((c) => formatChangePresentation(c, identity))
      .filter(Boolean)
      .map((c) =>
        c.mode === "set"
          ? `${c.field} — ${c.value}`
          : c.mode === "clear"
            ? `${c.field} removed — ${c.value}`
            : `${c.field}: ${c.old} → ${c.new}`,
      );
    if (changeParts.length) parts.push(changeParts.join("; "));
  }
  if (parts.length) return parts.join(" · ");
  if (p.record) {
    const label =
      p.name || p.title || (p.recordId != null ? `#${p.recordId}` : "");
    return `${p.record}${label ? `: ${label}` : ""}`;
  }
  return typeof raw === "string" ? raw : "";
}

export function ChangedDataView({ raw, parsed }) {
  const p = parsed || parseChangedData(raw);
  if (!p) return <span>—</span>;

  const meaningful = Array.isArray(p.changes)
    ? p.changes.filter(isMeaningfulChange)
    : [];
  const identity = identityFromSummary(p.summary, meaningful.length > 0);
  const facts = extraFacts({ ...p, summary: identity || "" });

  const extras = facts.length ? (
    <div className="log-report-facts">
      {facts.map((f) => (
        <div key={f.label} className="log-report-fact">
          <span className="log-report-fact-label">{f.label}:</span> {f.value}
        </div>
      ))}
    </div>
  ) : null;

  const presented = meaningful
    .map((c) => formatChangePresentation(c, identity))
    .filter(Boolean);

  if (presented.length) {
    return (
      <div className="log-report-changed">
        {identity && <div className="log-report-summary">{identity}</div>}
        <div className="log-report-diff-list">
          {presented.map((c, idx) =>
            c.mode === "change" ? (
              <div key={idx} className="log-report-diff">
                <span className="log-report-diff-field">{c.field}</span>
                <span className="log-report-diff-old">{c.old}</span>
                <span className="log-report-diff-arrow">→</span>
                <span className="log-report-diff-new">{c.new}</span>
              </div>
            ) : (
              <div key={idx} className="log-report-diff log-report-diff--set">
                <span className="log-report-diff-field">{c.field}</span>
                <span className="log-report-diff-sep">—</span>
                <span className="log-report-diff-new">{c.value}</span>
                {c.mode === "clear" ? (
                  <span className="log-report-diff-note">removed</span>
                ) : null}
              </div>
            ),
          )}
        </div>
        {extras}
      </div>
    );
  }

  if (p.success === true || p.success === false) {
    return (
      <div className="log-report-changed">
        <div className="log-report-summary">
          <span
            className={`log-report-action ${
              p.success
                ? "log-report-action--add"
                : "log-report-action--delete"
            } me-2`}
          >
            {p.success ? "Success" : "Failure"}
          </span>
          {identity || p.summary || "Notification sent"}
        </div>
        {extras}
      </div>
    );
  }

  if (identity || p.summary) {
    return (
      <div className="log-report-changed">
        <div className="log-report-summary" title={identity || p.summary}>
          {identity || p.summary}
        </div>
        {extras}
      </div>
    );
  }

  if (p.record) {
    const label =
      p.name || p.title || (p.recordId != null ? `#${p.recordId}` : "");
    return (
      <div className="log-report-changed">
        <span>
          {p.record}
          {label ? `: ${label}` : ""}
        </span>
        {extras}
      </div>
    );
  }

  if (extras) return extras;
  return <span>—</span>;
}

export function ModuleLinkCell({ row }) {
  const link = row.moduleLink;
  if (!link) return <span className="text-muted">{row.module || "—"}</span>;
  const internal = link.startsWith("/");
  if (internal) {
    return (
      <Link to={link.split("#")[0]} title={link} className="log-report-module-link">
        {row.module || link}
      </Link>
    );
  }
  return (
    <a href={link} target="_blank" rel="noreferrer" className="log-report-module-link">
      {row.module || link}
    </a>
  );
}
